import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import {
  Table, Button, Container, Row, Col, Form,
  Pagination, Spinner, Alert
} from 'react-bootstrap';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const SalesReport = () => {
  const [reportData, setReportData] = useState({
    groupedSales: [],
    summary: { total_sales: 0, total_orders: 0 },
    topProducts: [],
    bySize: []
  });

  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    grouping: 'daily',
    category: ''
  });

  const [categories, setCategories] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 10,
    total: 0
  });

  const [status, setStatus] = useState({
    loading: false,
    categoriesLoading: false,
    error: null,
    initialized: false
  });

  const reportRef = useRef();
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const fetchCategories = async () => {
      setStatus(prev => ({ ...prev, categoriesLoading: true }));
      try {
        const response = await axios.get(`${API_BASE}/api/reports/bread-categories`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('admin_token')}`
          }
        });
        setCategories(response.data.categories || []);
      } catch (error) {
        console.error('Failed to load categories:', error);
      } finally {
        setStatus(prev => ({ ...prev, categoriesLoading: false }));
      }
    };
    fetchCategories();
  }, []);

  const fetchReport = async () => {
    setStatus(prev => ({ ...prev, loading: true, error: null }));

    try {
      const params = {
        start: filters.startDate || undefined,
        end: filters.endDate || undefined,
        group: filters.grouping,
        category: filters.category || undefined,
        page: pagination.page,
        per_page: pagination.perPage
      };

      const response = await axios.get(`${API_BASE}/api/reports/sales/all`, {
        params,
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
      });

      setReportData({
        groupedSales: response.data.grouped_sales || [],
        summary: response.data.summary || { total_sales: 0, total_orders: 0 },
        topProducts: response.data.top_products || [],
        bySize: response.data.revenue_by_size || []
      });

      setPagination(prev => ({
        ...prev,
        total: response.data.pagination?.total || prev.total,
        page: response.data.pagination?.page || prev.page
      }));

      setStatus(prev => ({ ...prev, initialized: true }));
    } catch (error) {
      setStatus(prev => ({ ...prev, error: error.message }));
    } finally {
      setStatus(prev => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    fetchReport();
  }, [filters, pagination.page, pagination.perPage]);

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handlePerPageChange = (e) => {
    setPagination(prev => ({
      ...prev,
      perPage: Number(e.target.value),
      page: 1
    }));
  };

  const totalPages = Math.ceil(pagination.total / pagination.perPage) || 1;

  const renderPagination = () => (
    <Row className="mb-4 align-items-center">
      <Col md={2}>
        <Form.Select
          value={pagination.perPage}
          onChange={handlePerPageChange}
          disabled={status.loading}
        >
          <option value={5}>5 per page</option>
          <option value={10}>10 per page</option>
          <option value={20}>20 per page</option>
          <option value={50}>50 per page</option>
        </Form.Select>
      </Col>
      <Col md={8} className="d-flex justify-content-center">
        <Pagination>
          <Pagination.First
            onClick={() => handlePageChange(1)}
            disabled={pagination.page === 1 || status.loading}
          />
          <Pagination.Prev
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page === 1 || status.loading}
          />
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (pagination.page <= 3) {
              pageNum = i + 1;
            } else if (pagination.page >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = pagination.page - 2 + i;
            }
            return (
              <Pagination.Item
                key={pageNum}
                active={pageNum === pagination.page}
                onClick={() => handlePageChange(pageNum)}
                disabled={status.loading}
              >
                {pageNum}
              </Pagination.Item>
            );
          })}
          <Pagination.Next
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page >= totalPages || status.loading}
          />
          <Pagination.Last
            onClick={() => handlePageChange(totalPages)}
            disabled={pagination.page >= totalPages || status.loading}
          />
        </Pagination>
      </Col>
      <Col md={2} className="text-end">
        <span className="text-muted">
          Page {pagination.page} of {totalPages}
        </span>
      </Col>
    </Row>
  );

  const renderContent = () => {
    if (status.loading && !status.initialized) {
      return <Spinner animation="border" className="d-block mx-auto mt-5" />;
    }

    if (status.error) {
      return <Alert variant="danger">Error: {status.error}</Alert>;
    }

    return (
      <div ref={reportRef}>
        <div className="mb-4 p-3 bg-light rounded">
          <h4>Summary</h4>
          <Row>
            <Col md={6}><p><strong>Total Sales:</strong> ${reportData.summary.total_sales?.toFixed(2)}</p></Col>
            <Col md={6}><p><strong>Total Orders:</strong> {reportData.summary.total_orders}</p></Col>
          </Row>
        </div>

        {renderPagination()}

        <Table striped bordered hover>
          <thead className="table-dark">
            <tr>
              <th>{filters.grouping === 'monthly' ? 'Month' : filters.grouping === 'weekly' ? 'Week' : 'Date'}</th>
              <th>Orders</th>
              <th>Total Sales</th>
            </tr>
          </thead>
          <tbody>
            {reportData.groupedSales.map((item, idx) => (
              <tr key={idx}>
                <td>{item.date}</td>
                <td>{item.order_count}</td>
                <td>${item.total_sales.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </Table>

        <h5 className="mt-5">Top Selling Products</h5>
        <Table striped bordered hover>
          <thead className="table-dark">
            <tr>
              <th>Product</th>
              <th>Quantity</th>
              <th>Revenue</th>
            </tr>
          </thead>
          <tbody>
            {reportData.topProducts.length > 0 ? reportData.topProducts.map((p, i) => (
              <tr key={i}>
                <td>{p.bread_type}</td>
                <td>{p.total_quantity}</td>
                <td>${p.total_revenue.toFixed(2)}</td>
              </tr>
            )) : <tr><td colSpan="3">No data available</td></tr>}
          </tbody>
        </Table>

        <h5 className="mt-5">Revenue by Size</h5>
        <Table striped bordered hover>
          <thead className="table-dark">
            <tr>
              <th>Size</th>
              <th>Total Revenue</th>
            </tr>
          </thead>
          <tbody>
            {reportData.bySize.length > 0 ? reportData.bySize.map((s, i) => (
              <tr key={i}>
                <td>{s.size}</td>
                <td>${s.total_revenue.toFixed(2)}</td>
              </tr>
            )) : <tr><td colSpan="2">No data available</td></tr>}
          </tbody>
        </Table>
      </div>
    );
  };

  return (
    <Container className="py-4">
      <h2>Sales Report</h2>
      <Row className="mb-4 g-3">
        <Col md={2}>
          <Form.Label>Start Date</Form.Label>
          <Form.Control
            type="date"
            value={filters.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
          />
        </Col>
        <Col md={2}>
          <Form.Label>End Date</Form.Label>
          <Form.Control
            type="date"
            value={filters.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
          />
        </Col>
        <Col md={2}>
          <Form.Label>Group</Form.Label>
          <Form.Select
            value={filters.grouping}
            onChange={(e) => handleFilterChange('grouping', e.target.value)}
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Label>Category</Form.Label>
          <Form.Select
            value={filters.category}
            onChange={(e) => handleFilterChange('category', e.target.value)}
            disabled={status.categoriesLoading}
          >
            <option value="">All</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.name}>{cat.name}</option>
            ))}
          </Form.Select>
        </Col>
        <Col md={3} className="d-flex align-items-end justify-content-end">
          <Button variant="primary" onClick={fetchReport}>Refresh</Button>
          <Button
            className="ms-2"
            variant="secondary"
            onClick={() => {
              const input = reportRef.current;
              html2canvas(input).then((canvas) => {
                const pdf = new jsPDF('p', 'mm', 'a4');
                pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, 210, 297);
                pdf.save('sales-report.pdf');
              });
            }}
            disabled={status.loading || reportData.groupedSales.length === 0}
          >
            Export PDF
          </Button>
        </Col>
      </Row>

      {renderContent()}
    </Container>
  );
};

export default SalesReport;
